<script setup lang="ts">
import { computed, watch } from "vue"
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from "@headlessui/vue"
import { XMarkIcon } from "@heroicons/vue/24/outline"
import { useRoute } from "vue-router"
import SvgIcon from "@jamescoyle/vue-icon"
import NFansLogoInvert from "../../components/NFansLogoInvert.vue"
import auth from "../../auth/keycloak"
import { buildNavigationItems, DOCK_ROUTE_NAME_SET } from "./navigation"

const props = defineProps<{ open: boolean }>()
const emit = defineEmits<{ "update:open": [value: boolean] }>()

const route = useRoute()

const items = computed(() =>
  buildNavigationItems().filter(i => !DOCK_ROUTE_NAME_SET.has(i.routeName))
)

const username = computed(() => auth.userProfile.value?.username ?? "")
const fullName = computed(() => {
  const p = auth.userProfile.value ?? {}
  return [p.firstName, p.lastName].filter(Boolean).join(" ").trim()
})

function close() { emit("update:open", false) }
function logout() { auth.logout() }

// Close on route change so the drawer doesn't stick after navigating via a link.
watch(() => route.fullPath, () => { if (props.open) close() })
</script>

<template>
  <TransitionRoot :show="open" as="template">
    <Dialog as="div" class="relative z-40" @close="close">
      <TransitionChild
        as="template"
        enter="transition-opacity ease-linear duration-200"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="transition-opacity ease-linear duration-200"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-black/40" aria-hidden="true" />
      </TransitionChild>
      <div class="fixed inset-0 flex justify-end">
        <TransitionChild
          as="template"
          enter="transition ease-out duration-200 transform"
          enter-from="translate-x-full"
          enter-to="translate-x-0"
          leave="transition ease-in duration-200 transform"
          leave-from="translate-x-0"
          leave-to="translate-x-full"
        >
          <DialogPanel
            class="w-80 max-w-[85vw] h-full bg-neutral text-neutral-content flex flex-col shadow-2xl"
            data-testid="drawer-panel"
          >
            <div class="shrink-0 p-4 flex items-center gap-3 border-b border-white/10">
              <NFansLogoInvert class="h-8 w-auto" alt="NFans" />
              <div class="flex-1 min-w-0">
                <DialogTitle as="p" class="text-sm font-medium truncate">{{ fullName || username }}</DialogTitle>
                <p v-if="fullName" class="text-xs opacity-70 truncate">{{ username }}</p>
              </div>
              <button
                type="button"
                class="btn btn-ghost btn-sm btn-square"
                data-testid="drawer-close"
                @click="close"
              >
                <span class="sr-only">Schließen</span>
                <XMarkIcon class="h-5 w-5" aria-hidden="true" />
              </button>
            </div>
            <nav class="flex-1 overflow-y-auto">
              <ul class="menu menu-vertical w-full gap-1 p-2">
                <li v-for="item in items" :key="item.routeName">
                  <router-link :to="item.to" class="flex items-center gap-3 hover:bg-white/10">
                    <svg-icon
                      type="mdi"
                      :path="item.icon"
                      class="h-5 w-5 shrink-0"
                      aria-hidden="true"
                    />
                    <span class="truncate">{{ item.name }}</span>
                  </router-link>
                </li>
              </ul>
            </nav>
            <div class="shrink-0 border-t border-white/10 p-2 flex flex-col gap-1">
              <a :href="auth.accountUrl()" class="btn btn-ghost btn-sm justify-start">
                Profil
              </a>
              <button
                type="button"
                class="btn btn-ghost btn-sm justify-start"
                data-testid="drawer-logout"
                @click="logout"
              >
                Abmelden
              </button>
            </div>
          </DialogPanel>
        </TransitionChild>
      </div>
    </Dialog>
  </TransitionRoot>
</template>
